import clsx from "clsx";
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faCircleCheck,
  faCircleMinus,
  faCircleQuestion,
  faCircleXmark,
} from "@awesome.me/kit-70c1f0055f/icons/duotone/solid";
import { faSparkles, faUser, faPenToSquare } from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import { Button } from "@/components/ui/button/Button";
import {
  getCaseDocuments,
  patchProblemStatementHumanReviews,
  type CaseDocument,
  type HumanStatementCall,
  type ProblemStatementHumanReview,
  type ProblemStatementHumanReviewPatch,
  type ProblemStatementNarrative,
  type ProblemStatementRating,
  type ProblemStatementRollup,
  type ProblemStatementRuleSnapshot,
  type PsRuleHumanReview,
} from "@/services/api/case-checking";
import { useProblemStatementRun } from "@/components/case-checking/useProblemStatementRun";
import { StatementSupportingDocs } from "@/components/case-checking/StatementSupportingDocs";
import { RuleCitations } from "@/components/case-checking/RuleCitations";
import {
  CC_CONCERN_LIST,
  CC_CONCERN_TITLE,
  CC_ERROR_TEXT,
  CC_FIELD,
  CC_INSET,
  CC_LABEL_FAILED,
  CC_PANEL,
  CC_SECTION_LABEL,
  CC_SUCCESS_INSET,
  CC_SUCCESS_INSET_BODY,
  CC_SUCCESS_INSET_TITLE,
} from "@/components/case-checking/caseCheckingChrome";
import { CaseCheckingListRow } from "@/components/case-checking/CaseCheckingListRow";
import { ICON_CHECK_PASSED } from "@/components/case-checking/caseCheckingStatusBadges";
import {
  agentPillForRollup,
  clearHumanReviewPatch,
  emptyHumanReview,
  humanSignoffStatus,
  PILL_ACTIVE_CLASS,
  RATING_TILE_CLASS,
  RATING_TO_PILL,
  pillLabelForRating,
} from "@/components/case-checking/problemStatementUi";
import {
  agentStatusToHumanCall,
  normalizeRuleStatus,
} from "@/components/case-checking/problemStatementRuleIssues";
import { groupByGateway, sortRulesForDisplay } from "@/components/case-checking/problemStatementRuleDisplay";
import {
  applyRuleOverridesToRollup,
  collectPsRuleChangeQuotes,
  effectiveRuleStatus,
  ruleReviewKey,
  RULE_CALL_LABEL,
  type RuleOverrideMap,
} from "@/components/case-checking/humanRuleReviewCascade";
import { useHumanRuleReviews } from "@/components/case-checking/useHumanRuleReviews";

/** Rating → display pill key. Deterministic ratings map to the 4-option pill row. */
type PillCall = HumanStatementCall; // "yes" | "unclear" | "no" | "na"

const PILL_OPTIONS: { id: PillCall; label: string }[] = [
  { id: "yes", label: "Yes" },
  { id: "unclear", label: "Unclear" },
  { id: "no", label: "No" },
  { id: "na", label: "N/A" },
];

// ─────────────────────────────────────────────────────────────────────────
// Rule status icon
// ─────────────────────────────────────────────────────────────────────────

function ruleStatusIcon(status: string): { icon: typeof faCircleCheck; className: string } {
  const normalized = normalizeRuleStatus(status);
  if (normalized === "passed") return { icon: faCircleCheck, className: ICON_CHECK_PASSED };
  if (normalized === "failed") return { icon: faCircleXmark, className: CC_LABEL_FAILED };
  if (normalized === "na") return { icon: faCircleMinus, className: "text-slate-400" };
  return { icon: faCircleQuestion, className: "text-amber-400" };
}

function summariseRuleCounts(counts: Record<string, number>): string {
  const bits: string[] = [];
  if (counts.passed) bits.push(`${counts.passed} passed`);
  if (counts.failed) bits.push(`${counts.failed} failed`);
  if (counts.unclear) bits.push(`${counts.unclear} for review`);
  if (counts.na) bits.push(`${counts.na} N/A`);
  return bits.join(" · ") || "no rule outcomes yet";
}

// ─────────────────────────────────────────────────────────────────────────
// Small primitives
// ─────────────────────────────────────────────────────────────────────────

function PillRow({
  value,
  onChange,
  disabled = false,
  size = "md",
  ariaLabel = "Problem statement outcome",
}: {
  value: PillCall | null;
  onChange?: (next: PillCall) => void;
  disabled?: boolean;
  size?: "md" | "sm";
  ariaLabel?: string;
}) {
  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className={clsx(
        "grid grid-cols-4 gap-1.5 rounded-lg bg-slate-100 dark:bg-slate-900/40 p-1 ring-1 ring-slate-200 dark:ring-white/5",
        size === "sm" && "gap-1 p-0.5",
      )}
    >
      {PILL_OPTIONS.map((opt) => {
        const active = value === opt.id;
        return (
          <Button
            key={opt.id}
            plain
            type="button"
            disabled={disabled || !onChange}
            onClick={onChange ? () => onChange(opt.id) : undefined}
            className={clsx(
              "rounded-md font-medium ring-1 ring-inset transition-colors",
              size === "sm" ? "px-2 py-1 text-[11px]" : "px-3 py-1.5 text-xs",
              active
                ? PILL_ACTIVE_CLASS[opt.id]
                : "bg-transparent text-slate-400 ring-transparent hover:text-slate-700 dark:hover:text-slate-200",
              (disabled || !onChange) && "cursor-default opacity-90",
              !disabled && onChange && !active && "hover:bg-slate-100 dark:hover:bg-white/[0.04]",
            )}
            role="radio"
            aria-checked={active}
          >
            {opt.label}
          </Button>
        );
      })}
    </div>
  );
}

function ConfidenceBadge({ confidence }: { confidence: string | undefined }) {
  if (!confidence) return null;
  const label = confidence.charAt(0).toUpperCase() + confidence.slice(1);
  const tone =
    confidence === "high"
      ? "bg-emerald-100 text-emerald-800 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-200 dark:ring-emerald-500/30"
      : confidence === "low"
        ? "bg-rose-100 text-rose-800 ring-rose-200 dark:bg-rose-500/10 dark:text-rose-200 dark:ring-rose-500/30"
        : "bg-amber-100 text-amber-800 ring-amber-200 dark:bg-amber-500/10 dark:text-amber-200 dark:ring-amber-500/30";
  return (
    <span className={clsx("inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset", tone)}>
      <span className="uppercase tracking-wide text-[10px] text-slate-400">Confidence</span>
      <span>{label}</span>
    </span>
  );
}

function RuleRow({
  rule,
  highlighted = false,
  override,
  agentStatus: agentStatusProp,
  documentsById,
}: {
  rule: ProblemStatementRuleSnapshot;
  highlighted?: boolean;
  override?: PsRuleHumanReview;
  agentStatus?: string;
  documentsById?: Map<string, CaseDocument>;
}) {
  const [open, setOpen] = useState(highlighted);
  const effectiveNorm = effectiveRuleStatus(agentStatusProp ?? rule.status, override);
  const humanChanged = Boolean(override?.reviewed_at);
  const displayStatus =
    effectiveNorm === "passed"
      ? "passed"
      : effectiveNorm === "failed"
        ? "failed"
        : effectiveNorm === "na"
          ? "na"
          : "unclear";
  const { icon, className } = ruleStatusIcon(displayStatus);
  const citations = rule.citations ?? [];
  const hasCitations = citations.length > 0;
  const hasEvidence = Boolean(rule.evidence) || hasCitations;

  useEffect(() => {
    if (!highlighted) return;
    setOpen(true);
    const frame = requestAnimationFrame(() => {
      document
        .querySelector(`[data-testid="ps-rule-${rule.rule_id}"]`)
        ?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    });
    return () => cancelAnimationFrame(frame);
  }, [highlighted, rule.rule_id]);

  const footer =
    open && hasEvidence ? (
      <div className="border-t border-slate-200 px-3 py-2 text-xs text-slate-400 dark:border-white/5">
        {humanChanged ? (
          <p className="mb-1 text-[11px] text-violet-300">
            Agent had {RULE_CALL_LABEL[agentStatusToHumanCall(agentStatusProp ?? rule.status)]} — you set{" "}
            {RULE_CALL_LABEL[override!.call]}
          </p>
        ) : null}
        {override?.notes ? (
          <p className="mb-1 italic text-slate-600 dark:text-slate-300">&ldquo;{override.notes}&rdquo;</p>
        ) : null}
        {rule.evidence}
        <RuleCitations
          citations={citations}
          documentName={(id) => documentsById?.get(id)?.filename}
          testId={`ps-rule-citations-${rule.rule_id}`}
        />
      </div>
    ) : null;

  return (
    <li
      className={clsx(
        "rounded-lg ring-1 bg-slate-50 dark:bg-white/[0.02] transition-shadow duration-500",
        highlighted ? "ring-violet-400/50 shadow-[0_0_0_1px_rgba(167,139,250,0.25)]" : "ring-slate-200 dark:ring-white/5",
      )}
      data-testid={`ps-rule-${rule.rule_id}`}
    >
      <CaseCheckingListRow
        variant="flush"
        onClick={hasEvidence ? () => setOpen((v) => !v) : undefined}
        ariaExpanded={open}
        ariaLabel={
          hasEvidence
            ? `${rule.label || rule.rule_id}, ${open ? "collapse" : "expand"} evidence`
            : undefined
        }
        innerClassName="items-center px-3 py-2"
        footer={footer}
      >
        <FontAwesomeIcon icon={icon} className={clsx("h-4 w-4 shrink-0", className)} />
        <span className="flex-1 truncate text-sm text-slate-700 dark:text-slate-200">{rule.label || rule.rule_id}</span>
        {humanChanged && override ? (
          <span className="shrink-0 text-[10px] font-medium text-violet-300">
            {RULE_CALL_LABEL[override.call]}
          </span>
        ) : null}
        <span className="shrink-0 text-[11px] font-medium text-slate-500">{rule.rule_id}</span>
        {hasEvidence ? (
          <FontAwesomeIcon
            icon={faChevronDown}
            className={clsx("h-3 w-3 text-slate-500 transition-transform", open && "rotate-180")}
          />
        ) : null}
      </CaseCheckingListRow>
    </li>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Coverage summary sheet (top of tab)
// ─────────────────────────────────────────────────────────────────────────

function CoverageSummary({
  rollups,
  scrollToId,
}: {
  rollups: ProblemStatementRollup[];
  scrollToId: (id: string) => void;
}) {
  const counts = useMemo(() => {
    const c: Record<ProblemStatementRating, number> = {
      pass: 0,
      fail: 0,
      unclear: 0,
      na: 0,
      informational: 0,
    };
    for (const r of rollups) c[r.rating] += 1;
    return c;
  }, [rollups]);

  const summaryChips: { label: string; count: number; dot: string }[] = [
    { label: "Pass", count: counts.pass, dot: "bg-emerald-400" },
    { label: "For review", count: counts.unclear, dot: "bg-amber-400" },
    { label: "Fail", count: counts.fail, dot: "bg-rose-400" },
    { label: "N/A", count: counts.na, dot: "bg-slate-400" },
  ];

  return (
    <section className={clsx(CC_PANEL, "space-y-4")} data-testid="coverage-summary">
      <div className="flex flex-wrap items-baseline gap-3">
        <p className={clsx(CC_SECTION_LABEL, "!mb-0")}>Coverage review</p>
        <p className="text-xs text-slate-500">{rollups.length} outcomes</p>
      </div>

      <div className="flex flex-wrap gap-2">
        {summaryChips.map((chip) => (
          <span
            key={chip.label}
            className="inline-flex items-center gap-2 rounded-lg bg-slate-50 dark:bg-white/[0.03] px-3 py-1.5 text-xs text-slate-600 dark:text-slate-300 ring-1 ring-slate-200 dark:ring-white/5"
          >
            <span className={clsx("h-2 w-2 rounded-full", chip.dot)} />
            <span className="font-medium text-slate-900 dark:text-slate-100">{chip.count}</span>
            <span className="text-slate-500">{chip.label}</span>
          </span>
        ))}
      </div>

      {groupByGateway(rollups).map((group) => (
        <div key={group.gateway ?? "all"} className="space-y-1.5">
          {group.label ? <p className="text-[11px] font-medium text-slate-500">{group.label}</p> : null}
          <div
            className="grid gap-1.5"
            style={{ gridTemplateColumns: "repeat(auto-fill, minmax(84px, 1fr))" }}
            data-testid="coverage-tile-grid"
          >
            {group.items.map((r) => (
              <Button
                key={r.id}
                plain
                type="button"
                onClick={() => scrollToId(r.id)}
                className={clsx(
                  "flex flex-col items-start gap-1 rounded-lg px-2 py-1.5 text-left ring-1 ring-inset transition-transform hover:scale-[1.02]",
                  RATING_TILE_CLASS[r.rating],
                )}
                title={r.title}
                data-testid={`coverage-tile-${r.id}`}
              >
                <span className="text-[10px] font-semibold uppercase tracking-wide opacity-70">{r.id}</span>
                <span className="truncate text-[11px] font-medium leading-tight w-full">{r.title}</span>
              </Button>
            ))}
          </div>
        </div>
      ))}
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Statement card (agent view)
// ─────────────────────────────────────────────────────────────────────────

function AgentStatementCard({
  rollup,
  narrative,
  index,
  total,
}: {
  rollup: ProblemStatementRollup;
  narrative?: ProblemStatementNarrative;
  index: number;
  total: number;
}) {
  const pillValue = RATING_TO_PILL[rollup.rating];
  return (
    <article className={clsx(CC_PANEL, "space-y-4 scroll-mt-24")} data-testid={`ps-card-${rollup.id}`}>
      <header className="space-y-1.5">
        <div className="flex items-center justify-between gap-3">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
            Statement {index + 1} of {total} · {rollup.id}
          </p>
          <ConfidenceBadge confidence={narrative?.confidence} />
        </div>
        <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">{rollup.title}</h3>
        {rollup.assessment_question ? (
          <p className="text-sm text-slate-400">{rollup.assessment_question}</p>
        ) : null}
      </header>

      <div className="flex items-center gap-3">
        <span className="text-xs uppercase tracking-wide text-slate-500">Agent rating</span>
        <div className="flex-1">
          <PillRow value={pillValue} />
        </div>
      </div>

      {narrative?.summary ? (
        <div className="space-y-3 border-t border-slate-200 dark:border-white/5 pt-4">
          <p className="text-xs uppercase tracking-wide text-slate-500">Agent assessment</p>
          <p className="text-sm leading-relaxed text-slate-700 dark:text-slate-200">{narrative.summary}</p>
        </div>
      ) : null}

      {narrative && narrative.concerns.length > 0 ? (
        <div className="rounded-lg bg-amber-50 px-3 py-2.5 ring-1 ring-amber-200 dark:bg-amber-500/[0.06] dark:ring-amber-500/20">
          <p className={clsx("text-[11px]", CC_CONCERN_TITLE)}>Concerns raised</p>
          <ul className={clsx("mt-1.5 space-y-1 pl-4 text-xs", CC_CONCERN_LIST)}>
            {narrative.concerns.map((c) => (
              <li key={c}>{c}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {rollup.rules.length > 0 ? (
        <div className="space-y-2 border-t border-slate-200 dark:border-white/5 pt-4">
          <p className="text-xs text-slate-500">
            {rollup.rules.length} rules · {summariseRuleCounts(rollup.counts)}
          </p>
          <ul className="space-y-1.5">
            {sortRulesForDisplay(rollup.rules).map((rule) => (
              <RuleRow key={rule.rule_id} rule={rule} />
            ))}
          </ul>
        </div>
      ) : null}
    </article>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Peer sign-off card (side-by-side agent | human)
// ─────────────────────────────────────────────────────────────────────────

const REVIEW_COLUMN_CLASS = clsx(CC_INSET, "flex min-h-[20rem] flex-col p-4");

function ReviewColumnHeader({
  icon,
  iconClass,
  label,
}: {
  icon: typeof faSparkles;
  iconClass: string;
  label: string;
}) {
  return (
    <div className="mb-3 flex items-center gap-2 text-xs font-semibold text-slate-600 dark:text-slate-300">
      <FontAwesomeIcon icon={icon} className={clsx("h-3.5 w-3.5", iconClass)} />
      {label}
    </div>
  );
}

function ReviewColumnFooter({ children, leading }: { children: ReactNode; leading?: ReactNode }) {
  return (
    <div className="mt-auto flex items-center justify-between gap-2 pt-4">
      <div className="min-w-0">{leading}</div>
      <div className="flex shrink-0 justify-end gap-2">{children}</div>
    </div>
  );
}

type SaveState = "idle" | "saving" | "saved";

function PeerStatementCard({
  caseId,
  rollup,
  narrative,
  savedReview,
  index,
  total,
  onSave,
  saveState,
  focusedRuleId = null,
  readOnly = false,
  ruleOverrides,
}: {
  caseId: string;
  rollup: ProblemStatementRollup;
  narrative?: ProblemStatementNarrative;
  savedReview?: ProblemStatementHumanReview;
  index: number;
  total: number;
  onSave: (review: ProblemStatementHumanReviewPatch) => void;
  saveState: SaveState;
  focusedRuleId?: string | null;
  readOnly?: boolean;
  ruleOverrides: RuleOverrideMap;
}) {
  const { rollup: effectiveRollup } = useMemo(
    () => applyRuleOverridesToRollup(rollup, ruleOverrides),
    [rollup, ruleOverrides],
  );
  const ruleChanges = useMemo(
    () => collectPsRuleChangeQuotes(rollup, ruleOverrides),
    [rollup, ruleOverrides],
  );
  const agentPill = agentPillForRollup(rollup);
  const effectivePill = agentPillForRollup(effectiveRollup);
  const signoff = humanSignoffStatus(rollup, savedReview);
  const [choosing, setChoosing] = useState(false);
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [statementDocuments, setStatementDocuments] = useState<CaseDocument[] | undefined>(undefined);
  const [draft, setDraft] = useState<ProblemStatementHumanReview>(() =>
    emptyHumanReview(rollup.id, savedReview),
  );

  useEffect(() => {
    let cancelled = false;
    getCaseDocuments(caseId)
      .then((docs) => {
        if (!cancelled) setStatementDocuments(docs ?? []);
      })
      .catch(() => {
        if (!cancelled) setStatementDocuments([]);
      });
    return () => {
      cancelled = true;
    };
  }, [caseId]);

  const documentsById = useMemo(
    () => new Map((statementDocuments ?? []).map((doc) => [doc.id, doc])),
    [statementDocuments],
  );

  useEffect(() => {
    setChoosing(false);
    setOverrideOpen(false);
    setDraft(emptyHumanReview(rollup.id, savedReview));
  }, [rollup.id, savedReview]);

  const showChoose = (signoff === "pending" || choosing) && !overrideOpen;
  const showSaved = signoff !== "pending" && !choosing && !overrideOpen;
  const isSaving = saveState === "saving";

  const handleAgree = () => {
    if (!agentPill) return;
    onSave({
      problem_statement_id: rollup.id,
      call: agentPill,
      notes: "",
      reviewer_name: savedReview?.reviewer_name ?? "",
      reviewed_at: null,
    });
    setChoosing(false);
  };

  const openOverride = () => {
    setDraft(emptyHumanReview(rollup.id, savedReview));
    setOverrideOpen(true);
    setChoosing(false);
  };

  const handleSaveOverride = () => {
    onSave(draft);
    setOverrideOpen(false);
  };

  const cancelOverride = () => {
    setDraft(emptyHumanReview(rollup.id, savedReview));
    setOverrideOpen(false);
    if (signoff === "pending") setChoosing(false);
  };

  const handleClearReview = () => {
    if (
      !window.confirm(
        "Clear your review for this statement? It will show as review pending again. Your previous rating is kept in the audit trail.",
      )
    ) {
      return;
    }
    onSave(clearHumanReviewPatch(rollup.id));
    setChoosing(false);
    setOverrideOpen(false);
  };

  return (
    <article className={clsx(CC_PANEL, "space-y-4 scroll-mt-24")} data-testid={`ps-card-${rollup.id}`}>
      <header className="space-y-1.5">
        <div className="flex items-center justify-between gap-3">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
            Statement {index + 1} of {total} · {rollup.id}
          </p>
          <div className="flex items-center gap-2">
            <ConfidenceBadge confidence={narrative?.confidence} />
            <SaveIndicator state={saveState} />
          </div>
        </div>
        <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">{rollup.title}</h3>
        {rollup.assessment_question ? (
          <p className="text-sm text-slate-400">{rollup.assessment_question}</p>
        ) : null}
      </header>

      <div className="grid gap-3 md:grid-cols-2 md:items-stretch">
        <div className={REVIEW_COLUMN_CLASS}>
          <ReviewColumnHeader icon={faSparkles} iconClass="text-indigo-600 dark:text-indigo-300" label="Agent" />
          <PillRow value={agentPill} />
          <div className="mt-3 flex min-h-0 flex-1 flex-col gap-3">
            {narrative?.summary ? (
              <p className="text-sm leading-relaxed text-slate-700 dark:text-slate-200">{narrative.summary}</p>
            ) : (
              <p className="text-sm text-slate-500">No agent narrative for this statement.</p>
            )}
            {narrative && narrative.concerns.length > 0 ? (
              <div className="rounded-md bg-amber-50 px-3 py-2 ring-1 ring-amber-200 dark:bg-amber-500/[0.06] dark:ring-amber-500/20">
                <p className={clsx("text-[10px]", CC_CONCERN_TITLE)}>Concerns raised</p>
                <ul className={clsx("mt-1 space-y-0.5 pl-4 text-[11px]", CC_CONCERN_LIST)}>
                  {narrative.concerns.map((c) => (
                    <li key={c}>{c}</li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        </div>

        <div className={REVIEW_COLUMN_CLASS} data-testid={`ps-human-${rollup.id}`}>
          <ReviewColumnHeader
            icon={faUser}
            iconClass="text-violet-300"
            label={readOnly ? "Human review" : "Your review"}
          />

          {readOnly ? (
            ruleChanges.length > 0 ? (
              <>
                <PillRow value={effectivePill} disabled />
                <p className="mt-2 text-xs text-slate-500">
                  After your rule changes
                  {effectiveRollup.rating !== rollup.rating
                    ? ` · statement now ${pillLabelForRating(effectiveRollup.rating)}`
                    : ""}
                </p>
                <div className="mt-3 flex min-h-0 flex-1 flex-col gap-3">
                  {ruleChanges.map((change) => (
                    <blockquote
                      key={change.ruleId}
                      className="border-l-2 border-violet-400/40 pl-3 text-xs"
                    >
                      <p className="font-medium text-slate-700 dark:text-slate-200">{change.label}</p>
                      <p className="mt-0.5 text-violet-200">
                        {change.verdictChanged
                          ? `${RULE_CALL_LABEL[change.humanCall]} (agent: ${RULE_CALL_LABEL[change.agentCall]})`
                          : RULE_CALL_LABEL[change.humanCall]}
                      </p>
                      {change.note ? (
                        <p className="mt-1 italic text-slate-400">&ldquo;{change.note}&rdquo;</p>
                      ) : null}
                    </blockquote>
                  ))}
                </div>
              </>
            ) : (
              <>
                <PillRow value={savedReview?.call ?? null} disabled />
                <div className="mt-3 flex min-h-0 flex-1 flex-col gap-3">
                  {signoff === "pending" ? (
                    <p className="text-sm text-slate-500">Agreed with agent on all rules.</p>
                  ) : savedReview?.notes ? (
                    <p className="text-sm leading-relaxed text-slate-600 dark:text-slate-300">{savedReview.notes}</p>
                  ) : signoff === "agreed" ? (
                    <p className={clsx("text-sm", CC_SUCCESS_INSET_TITLE)}>Agreed with agent.</p>
                  ) : (
                    <p className="text-sm italic text-slate-500">No override notes.</p>
                  )}
                </div>
              </>
            )
          ) : null}

          {!readOnly && overrideOpen ? (
            <>
              <PillRow
                value={draft.call}
                onChange={(call) => setDraft((prev) => ({ ...prev, call }))}
              />
              <div className="mt-3 flex min-h-0 flex-1 flex-col">
                <label htmlFor={`ps-override-notes-${rollup.id}`} className="sr-only">
                  Reason for overriding problem statement outcome
                </label>
                <textarea
                  id={`ps-override-notes-${rollup.id}`}
                  className={clsx(
                    CC_FIELD,
                    "min-h-[10rem] flex-1 resize-none py-3 leading-relaxed focus:border-indigo-500/40 focus:outline-none focus:ring-1 focus:ring-indigo-500/30",
                  )}
                  placeholder="Why are you overriding the agent? (optional)"
                  value={draft.notes}
                  onChange={(e) => setDraft((prev) => ({ ...prev, notes: e.target.value }))}
                />
              </div>
              <ReviewColumnFooter>
                <Button plain onClick={cancelOverride} disabled={isSaving}>
                  Cancel
                </Button>
                <Button
                  color="indigo"
                  onClick={handleSaveOverride}
                  disabled={isSaving}
                  data-testid={`ps-save-${rollup.id}`}
                >
                  {isSaving ? "Saving…" : "Save override"}
                </Button>
              </ReviewColumnFooter>
            </>
          ) : null}

          {!readOnly && showChoose ? (
            <>
              <PillRow value={null} disabled />
              <div className="mt-3 flex min-h-0 flex-1 flex-col">
                <p className="text-sm leading-relaxed text-slate-400">
                  {signoff === "pending"
                    ? "Do you agree with the agent's rating for this statement?"
                    : "Choose how you'd like to update your review."}
                </p>
              </div>
              <ReviewColumnFooter
                leading={
                  signoff !== "pending" ? (
                    <Button
                      plain
                      onClick={handleClearReview}
                      disabled={isSaving}
                      data-testid={`ps-clear-${rollup.id}`}
                    >
                      Clear review
                    </Button>
                  ) : null
                }
              >
                {choosing && signoff !== "pending" ? (
                  <Button plain onClick={() => setChoosing(false)} disabled={isSaving}>
                    Cancel
                  </Button>
                ) : null}
                <Button outline onClick={openOverride} disabled={isSaving} data-testid={`ps-override-${rollup.id}`}>
                  Override
                </Button>
                <Button
                  color="indigo"
                  onClick={handleAgree}
                  disabled={!agentPill || isSaving}
                  data-testid={`ps-agree-${rollup.id}`}
                >
                  Agree with agent
                </Button>
              </ReviewColumnFooter>
            </>
          ) : null}

          {!readOnly && showSaved && signoff === "agreed" ? (
            <>
              <PillRow value={agentPill} disabled />
              <div className="mt-3 flex min-h-0 flex-1 flex-col gap-3">
                <div className={clsx("flex items-start gap-2", CC_SUCCESS_INSET, "px-3 py-2.5")}>
                  <FontAwesomeIcon icon={faCircleCheck} className={clsx("mt-0.5 h-4 w-4 shrink-0", ICON_CHECK_PASSED)} />
                  <div>
                    <p className={CC_SUCCESS_INSET_TITLE}>Agreed with agent</p>
                    <p className={clsx("mt-0.5 text-xs", CC_SUCCESS_INSET_BODY)}>
                      Your rating matches the agent assessment.
                    </p>
                  </div>
                </div>
              </div>
              <ReviewColumnFooter
                leading={
                  <Button
                    plain
                    onClick={handleClearReview}
                    disabled={isSaving}
                    data-testid={`ps-clear-${rollup.id}`}
                  >
                    Clear review
                  </Button>
                }
              >
                <Button outline onClick={() => setChoosing(true)} icon={<FontAwesomeIcon icon={faPenToSquare} />}>
                  Edit review
                </Button>
              </ReviewColumnFooter>
            </>
          ) : null}

          {!readOnly && showSaved && signoff === "overridden" ? (
            <>
              <PillRow value={savedReview?.call ?? null} disabled />
              <div className="mt-3 flex min-h-0 flex-1 flex-col">
                {savedReview?.notes ? (
                  <p className="text-sm leading-relaxed text-slate-600 dark:text-slate-300">{savedReview.notes}</p>
                ) : (
                  <p className="text-sm italic text-slate-500">No override notes provided.</p>
                )}
              </div>
              <ReviewColumnFooter
                leading={
                  <Button
                    plain
                    onClick={handleClearReview}
                    disabled={isSaving}
                    data-testid={`ps-clear-${rollup.id}`}
                  >
                    Clear review
                  </Button>
                }
              >
                <Button
                  outline
                  onClick={openOverride}
                  icon={<FontAwesomeIcon icon={faPenToSquare} />}
                  data-testid={`ps-edit-${rollup.id}`}
                >
                  Edit override
                </Button>
              </ReviewColumnFooter>
            </>
          ) : null}
        </div>
      </div>

      {rollup.rules.length > 0 ? (
        <div className="space-y-2 border-t border-slate-200 dark:border-white/5 pt-4">
          <p className="text-xs text-slate-500">
            Shared evidence · {effectiveRollup.rules.length} rules · {summariseRuleCounts(effectiveRollup.counts)}
          </p>
          <ul className="space-y-1.5">
            {sortRulesForDisplay(effectiveRollup.rules).map((rule) => {
              const agentRule = rollup.rules.find((r) => r.rule_id === rule.rule_id);
              return (
                <RuleRow
                  key={rule.rule_id}
                  rule={rule}
                  override={ruleOverrides.get(ruleReviewKey(rollup.id, rule.rule_id))}
                  agentStatus={agentRule?.status}
                  highlighted={focusedRuleId === rule.rule_id}
                  documentsById={documentsById}
                />
              );
            })}
          </ul>
          <StatementSupportingDocs
            caseId={caseId}
            rules={rollup.rules}
            documents={statementDocuments ?? []}
            documentsLoading={statementDocuments === undefined}
          />
        </div>
      ) : null}
    </article>
  );
}

function SaveIndicator({ state }: { state: SaveState }) {
  if (state === "idle") return null;
  return (
    <span
      className={clsx(
        "inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-[10px] font-medium ring-1 ring-inset",
        state === "saving"
          ? "bg-slate-100 text-slate-700 dark:bg-slate-500/15 dark:text-slate-300 ring-slate-400/30"
          : "bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-200 ring-emerald-500/30",
      )}
    >
      {state === "saving" ? "Saving…" : "Saved"}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Tab component
// ─────────────────────────────────────────────────────────────────────────

export interface ProblemStatementsTabProps {
  caseId: string;
  peerReviewMode?: boolean;
  /** Show only the focused statement card (carousel deepdive). */
  deepdiveOnly?: boolean;
  focusedStatementId?: string | null;
  /** Expand and highlight a rule row (deep-link from summary rule issues). */
  focusedRuleId?: string | null;
  /** View-only — hide sign-off controls (Case Summary deepdive). */
  readOnly?: boolean;
}

/** Explicit save for peer sign-off (no autosave). */
export function ProblemStatementsTab({
  caseId,
  peerReviewMode = false,
  deepdiveOnly = false,
  focusedStatementId = null,
  focusedRuleId = null,
  readOnly = false,
}: ProblemStatementsTabProps) {
  const { run, setRun, loading, error, setError } = useProblemStatementRun(caseId);
  const [saveState, setSaveState] = useState<Record<string, SaveState>>({});
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { overridesByKey, reloadToken } = useHumanRuleReviews(caseId);

  useEffect(() => {
    setSaveState({});
  }, [run?.id]);

  const narrativesById = useMemo(() => {
    const map = new Map<string, ProblemStatementNarrative>();
    for (const n of run?.narratives ?? []) map.set(n.problem_statement_id, n);
    return map;
  }, [run?.narratives]);

  const savedReviewsById = useMemo(() => {
    const map = new Map<string, ProblemStatementHumanReview>();
    for (const r of run?.human_reviews ?? []) map.set(r.problem_statement_id, r);
    return map;
  }, [run?.human_reviews]);

  const scorable = useMemo(
    () => (run?.rollups ?? []).filter((r) => r.rollup_kind !== "final"),
    [run?.rollups],
  );

  const scrollToId = useCallback((id: string) => {
    const el = document.querySelector(`[data-testid="ps-card-${id}"]`);
    if (el instanceof HTMLElement) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const saveHumanReview = useCallback(
    async (statementId: string, review: ProblemStatementHumanReviewPatch) => {
      if (!run) return;
      setSaveState((s) => ({ ...s, [statementId]: "saving" }));
      try {
        const updated = await patchProblemStatementHumanReviews(caseId, run.id, [review]);
        setRun(updated);
        setSaveState((s) => ({ ...s, [statementId]: "saved" }));
        setTimeout(() => {
          setSaveState((s) => (s[statementId] === "saved" ? { ...s, [statementId]: "idle" } : s));
        }, 2000);
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to save sign-off");
        setSaveState((s) => ({ ...s, [statementId]: "idle" }));
      }
    },
    [caseId, run, setRun, setError],
  );

  const visibleScorable = useMemo(() => {
    if (!deepdiveOnly || !focusedStatementId) return scorable;
    const row = scorable.find((r) => r.id === focusedStatementId);
    return row ? [row] : scorable.slice(0, 1);
  }, [deepdiveOnly, focusedStatementId, scorable]);

  if (loading) return <p className="text-sm text-slate-400">Loading coverage review…</p>;

  if (!run) {
    return (
      <div className={CC_PANEL} data-testid="problem-statements-empty">
        <p className="text-sm text-slate-600 dark:text-slate-300">No problem-statement run yet.</p>
        <p className="mt-1 text-xs text-slate-500">
          The agent saves narratives after the problem-statements mission step completes.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="problem-statements-tab" ref={containerRef}>
      {!deepdiveOnly ? <CoverageSummary rollups={scorable} scrollToId={scrollToId} /> : null}

      {error ? <p className={clsx("text-sm", CC_ERROR_TEXT)}>{error}</p> : null}

      {groupByGateway(visibleScorable).map((group) => (
      <div key={group.gateway ?? "all"} className="space-y-3">
        {group.label && !deepdiveOnly ? <p className={clsx(CC_SECTION_LABEL, "!mb-0")}>{group.label}</p> : null}
        {group.items.map((rollup, index) => {
          const cardIndex = scorable.findIndex((r) => r.id === rollup.id);
          return peerReviewMode ? (
            <PeerStatementCard
              key={`${rollup.id}-${reloadToken ?? "none"}`}
              caseId={caseId}
              rollup={rollup}
              narrative={narrativesById.get(rollup.id)}
              savedReview={savedReviewsById.get(rollup.id)}
              index={cardIndex >= 0 ? cardIndex : index}
              total={scorable.length}
              onSave={(review) => void saveHumanReview(rollup.id, review)}
              saveState={saveState[rollup.id] ?? "idle"}
              focusedRuleId={focusedRuleId}
              readOnly={readOnly}
              ruleOverrides={overridesByKey}
            />
          ) : (
            <AgentStatementCard
              key={rollup.id}
              rollup={rollup}
              narrative={narrativesById.get(rollup.id)}
              index={cardIndex >= 0 ? cardIndex : index}
              total={scorable.length}
            />
          );
        })}
      </div>
      ))}
    </div>
  );
}
